import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { Inline } from '../ui/Inline';
import Store from '../../store/Store';
import { BigIconClose, TickCircle, IconCloseOrange } from '../Icons';
import { Button } from '../ui/Button';

const IconColors = {
    ok: 'bg-green-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
} as const;

export type IconColorType = keyof typeof IconColors;

export type PopupDialogMessage = {
    type: IconColorType;
    title: string;
    text?: string;
    content?: ReactNode;
    icon?: ReactNode; // custom icon to display in dialog header
    buttons?: string[];
    time?: number;
    ok?: () => void;
    cancel?: () => void;
    onAction?: (action: string) => void;
};

export type PopupMessageType = [IconColorType, string, string];

export const PopupDialog = () => {
    const [message, setMessage] = useState<PopupDialogMessage | undefined>(undefined);

    useEffect(() => {
        let timeout: NodeJS.Timeout | undefined;
        Store.setPopupDialog = (opts: PopupDialogMessage | undefined) => {
            clearTimeout(timeout);
            if (!opts) return setMessage(undefined);
            setMessage(opts);
            if (opts.time)
                timeout = setTimeout(() => {
                    setMessage(undefined);
                }, opts.time * 1000);
        };
    }, []);

    if (!message) return null;

    return (
        <div className={'popup-dialog'}>
            <div className="popup-body">
                <div className="flex items-center mb-3">
                    <span className="mr-3 flex items-center">
                        {message.icon !== undefined ? message.icon : <TickCircle />}
                    </span>
                    <div className="font-bold">{message.title}</div>
                </div>

                {message.text && <div>{message.text}</div>}

                {message.content}

                <button
                    className="popup-message-close"
                    data-pw="popup-message-close"
                    onClick={() => {
                        setMessage(undefined);
                    }}>
                    <BigIconClose />
                </button>

                <div className="flex flex-row gap-4 mt-8 justify-center">
                    {(() => {
                        const btns = message.buttons ?? ['OK'];
                        if (btns.length === 1) {
                            // Single button: orange, white text, no icon, centered
                            return [
                                <div className='justify-items-end w-full'>
                                    <Button
                                        key={0}
                                        primary
                                        className='max-w-[50%]'
                                        data-pw="popup-button"
                                        onClick={() => {
                                            setMessage(undefined);
                                            if (btns[0] === 'OK') message.ok?.();
                                            if (btns[0] === 'Abbrechen') message.cancel?.();
                                            message.onAction?.(btns[0]);
                                        }}
                                    >
                                        {btns[0]}
                                    </Button>
                                </div>
                            ];
                        } else {
                            // Two buttons: left is cancel (light, icon), right is confirm (orange, no icon)
                            return [

                                <Button
                                    key={0}
                                    className="border border-[#E1320F] text-[#E1320F] bg-white gap-2"
                                    data-pw="popup-button"
                                    onClick={() => {
                                        setMessage(undefined);
                                        if (btns[1] === 'OK') message.ok?.();
                                        if (btns[1] === 'Abbrechen') message.cancel?.();
                                        message.onAction?.(btns[0]);
                                    }}
                                >
                                    <IconCloseOrange /> {btns[0]}
                                </Button>
                                ,
                                <Button
                                    key={1}
                                    primary
                                    data-pw="popup-button"
                                    onClick={() => {
                                        setMessage(undefined);
                                        if (btns[1] === 'OK') message.ok?.();
                                        if (btns[1] === 'Abbrechen') message.cancel?.();
                                        message.onAction?.(btns[1]);
                                    }}
                                >
                                    {btns[1]}
                                </Button>
                            ];
                        }
                    })()}
                </div>
            </div>
        </div>
    );
};

export const PopupMessage = () => {
    const [message, setMessage] = useState<PopupMessageType | undefined>(undefined);
    const [isOn, setIsOn] = useState(false);
    const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        Store.setPopupMessage = (
            type: IconColorType,
            title: string,
            message: string,
            time = 4000
        ) => {
            clearTimeout(timeout.current);

            if (!title && !message) {
                setIsOn(false);
                setMessage(undefined);
                return;
            }
            setMessage([type, title, message]);
            setIsOn(true);

            if (time) timeout.current = setTimeout(() => setIsOn(false), time);
        };
    }, []);

    if (!message) return null;

    return (
        <div className={'popup-message ' + (isOn ? 'on' : 'off')}
            onMouseEnter={() => clearTimeout(timeout.current)}
            onMouseLeave={() => timeout.current = setTimeout(() => setIsOn(false), 2000)}
        >
            <div className="flex items-center mb-2">
                {IconColors[message[0]] && (
                    <div
                        className={
                            IconColors[message[0]] +
                            ' w-5 h-5 rounded-full mr-3'
                        }></div>
                )}
                <div className="font-bold">{message[1]}</div>
            </div>
            <div className="mt-2">{message[2]}</div>

            {isOn && (
                <button
                    className="popup-message-close"
                    data-pw="popup-message-close"
                    onClick={() => {
                        setIsOn(false);
                        setMessage(undefined);
                    }}>
                    <BigIconClose />
                </button>
            )}
        </div>
    );
};
